import React, { FC, ReactElement, useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ShoppingOutlined, RightOutlined, FilterOutlined, CloseOutlined, LeftOutlined } from "@ant-design/icons";
import { Spin, DatePicker, Select, Input } from "antd";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { BASE_URL } from "../../../constants/urlConstants";

import {
  selectIsOrdersLoading,
  selectOrders,
} from "../../../redux-toolkit/orders/orders-selector";
import {
  fetchAllUsersOrders,
  fetchDistributorOrders,
} from "../../../redux-toolkit/orders/orders-thunks";
import { resetOrders } from "../../../redux-toolkit/orders/orders-slice";
import OrdersTable from "../../../components/OrdersTable/OrdersTable";
import "./OrdersList.css";

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const { Option } = Select;

/* ── Constants ──────────────────────────────────────────────── */
const PAGE_SIZE = 20; // Flipkart-style page size

/* ── Status config ─────────────────────────────────────────── */
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  REQUESTED:  { label: "Requested",  color: "#f57f17", bg: "#fffde7" },
  PENDING:    { label: "Pending",    color: "#e65100", bg: "#fff3e0" },
  ORDERED:    { label: "Ordered",    color: "#1565c0", bg: "#e3f2fd" },
  CONFIRMED:  { label: "Confirmed",  color: "#1565c0", bg: "#e3f2fd" },
  PROCESSING: { label: "Processing", color: "#6a1b9a", bg: "#f3e5f5" },
  SHIPPED:    { label: "Shipped",    color: "#00695c", bg: "#e0f2f1" },
  DELIVERED:  { label: "Delivered",  color: "#2e7d32", bg: "#e8f5e9" },
  FULFILLED:  { label: "Fulfilled",  color: "#2e7d32", bg: "#e8f5e9" },
  CANCELLED:  { label: "Cancelled",  color: "#c62828", bg: "#ffebee" },
  RETURNED:   { label: "Returned",   color: "#4e342e", bg: "#efebe9" },
};

const getStatusConfig = (status: string) =>
  STATUS_CONFIG[status?.toUpperCase()] ?? { label: status ?? "Unknown", color: "#555", bg: "#f5f5f5" };

const na = (val: any): string =>
  val !== null && val !== undefined && String(val).trim() !== "" ? String(val) : "N/A";

const extractOrders = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
};

/**
 * Robustly parse a date value that may be:
 *  - ISO string:          "2026-04-18T09:53:51.736078"
 *  - Space-separated:     "2026-04-18 09:53:51.736078"
 *  - Unix timestamp (ms): 1713433431736
 *  - Already a Date obj
 */
const parseOrderDate = (raw: any): Dayjs | null => {
  if (!raw) return null;
  if (raw instanceof Date) return dayjs(raw);
  if (typeof raw === "number") return dayjs(raw);
  if (typeof raw === "string") {
    // Normalise space-separated datetime to ISO format for dayjs
    const normalised = raw.trim().replace(" ", "T");
    const d = dayjs(normalised);
    if (d.isValid()) return d;
  }
  return null;
};

const formatOrderDate = (raw: any): string => {
  const d = parseOrderDate(raw);
  if (!d) return "—";
  return d.format("D MMM YYYY, h:mm A");
};

/* ── Filter State ───────────────────────────────────────────── */
interface FilterState {
  dateRange: [Dayjs | null, Dayjs | null] | null;
  postIndex: string;
  deliveryStatus: string;
  city: string;
  paymentType: string; // "COD" | "ONLINE" | ""
}

const DEFAULT_FILTERS: FilterState = {
  dateRange: null,
  postIndex: "",
  deliveryStatus: "",
  city: "",
  paymentType: "",
};

/* ── Apply filters to orders ────────────────────────────────── */
const applyFilters = (orders: any[], filters: FilterState): any[] => {
  return orders.filter((order) => {
    // Date range — use robust parser
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const raw = order.createdAt ?? order.date ?? order.orderDate;
      const orderDate = parseOrderDate(raw);
      if (!orderDate) return false; // can't determine date → exclude when filtering
      if (
        orderDate.isBefore(filters.dateRange[0].startOf("day")) ||
        orderDate.isAfter(filters.dateRange[1].endOf("day"))
      ) {
        return false;
      }
    }

    // Post index
    if (filters.postIndex.trim()) {
      const pi = String(order.postIndex ?? "").toLowerCase();
      if (!pi.includes(filters.postIndex.trim().toLowerCase())) return false;
    }

    // Delivery status
    if (filters.deliveryStatus) {
      const status = (order.deliveryStatus ?? order.orderStatus ?? order.status ?? "").toUpperCase();
      if (status !== filters.deliveryStatus) return false;
    }

    // City
    if (filters.city.trim()) {
      const city = String(order.city ?? "").toLowerCase();
      if (!city.includes(filters.city.trim().toLowerCase())) return false;
    }

    // Payment type
    if (filters.paymentType) {
      const pt = (order.paymentType ?? order.paymentMethod ?? "").toUpperCase();
      if (pt !== filters.paymentType) return false;
    }

    return true;
  });
};

/* ── Pagination helpers ─────────────────────────────────────── */
const paginate = <T,>(items: T[], page: number, pageSize: number): T[] => {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

const totalPages = (total: number, pageSize: number) =>
  Math.max(1, Math.ceil(total / pageSize));

/* ── Flipkart-style Pagination Component ───────────────────── */
interface PaginationProps {
  current: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
}

const FlipkartPagination: FC<PaginationProps> = ({ current, total, pageSize, onChange }) => {
  const pages = totalPages(total, pageSize);

  const start = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end   = Math.min(current * pageSize, total);

  // Build page number array with ellipsis
  const getPageNumbers = (): (number | "...")[] => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const nums: (number | "...")[] = [];
    if (current <= 4) {
      nums.push(1, 2, 3, 4, 5, "...", pages);
    } else if (current >= pages - 3) {
      nums.push(1, "...", pages - 4, pages - 3, pages - 2, pages - 1, pages);
    } else {
      nums.push(1, "...", current - 1, current, current + 1, "...", pages);
    }
    return nums;
  };

  return (
    <div className="ol-pagination">
      <span className="ol-pagination-info">
        {start}–{end} of {total} orders
      </span>

      <div className="ol-pagination-controls">
        <button
          className="ol-page-btn ol-page-nav"
          disabled={current === 1}
          onClick={() => onChange(current - 1)}
        >
          <LeftOutlined style={{ fontSize: 11 }} /> Prev
        </button>

        {getPageNumbers().map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} className="ol-page-ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`ol-page-btn ${current === p ? "active" : ""}`}
              onClick={() => onChange(p as number)}
            >
              {p}
            </button>
          )
        )}

        <button
          className="ol-page-btn ol-page-nav"
          disabled={current === pages}
          onClick={() => onChange(current + 1)}
        >
          Next <RightOutlined style={{ fontSize: 11 }} />
        </button>
      </div>
    </div>
  );
};

/* ── Filter Bar ─────────────────────────────────────────────── */
interface FilterBarProps {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  onReset: () => void;
  totalShown: number;
  totalAll: number;
}

const FilterBar: FC<FilterBarProps> = ({ filters, onChange, onReset, totalShown, totalAll }) => {
  const hasActiveFilter =
    !!filters.dateRange ||
    !!filters.postIndex ||
    !!filters.deliveryStatus ||
    !!filters.city ||
    !!filters.paymentType;

  return (
    <div className="ol-filter-bar">
      <div className="ol-filter-row">
        <div className="ol-filter-icon-label">
          <FilterOutlined className="ol-filter-icon" />
          <span className="ol-filter-label">Filters</span>
        </div>

        <RangePicker
          className="ol-filter-datepicker"
          value={filters.dateRange as any}
          onChange={(val) => onChange({ ...filters, dateRange: val as any })}
          size="small"
          format="DD MMM YYYY"
          placeholder={["From date", "To date"]}
        />

        <Input
          className="ol-filter-input"
          placeholder="Post Index"
          size="small"
          value={filters.postIndex}
          onChange={(e) => onChange({ ...filters, postIndex: e.target.value })}
          allowClear
        />

        <Select
          className="ol-filter-select"
          placeholder="Delivery Status"
          size="small"
          value={filters.deliveryStatus || undefined}
          onChange={(val) => onChange({ ...filters, deliveryStatus: val ?? "" })}
          allowClear
          onClear={() => onChange({ ...filters, deliveryStatus: "" })}
        >
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <Option key={key} value={key}>
              <span style={{ color: cfg.color, fontWeight: 600 }}>{cfg.label}</span>
            </Option>
          ))}
        </Select>

        <Input
          className="ol-filter-input"
          placeholder="City"
          size="small"
          value={filters.city}
          onChange={(e) => onChange({ ...filters, city: e.target.value })}
          allowClear
        />

        {/* NEW: Payment Type filter */}
        <Select
          className="ol-filter-select"
          placeholder="Payment Type"
          size="small"
          value={filters.paymentType || undefined}
          onChange={(val) => onChange({ ...filters, paymentType: val ?? "" })}
          allowClear
          onClear={() => onChange({ ...filters, paymentType: "" })}
        >
          <Option value="COD">
            <span style={{ color: "#e65100", fontWeight: 600 }}>Cash on Delivery</span>
          </Option>
          <Option value="ONLINE">
            <span style={{ color: "#1565c0", fontWeight: 600 }}>Online Payment</span>
          </Option>
        </Select>

        {hasActiveFilter && (
          <button className="ol-filter-reset" onClick={onReset}>
            <CloseOutlined style={{ fontSize: 10 }} /> Reset
          </button>
        )}
      </div>

      {hasActiveFilter && (
        <div className="ol-filter-result-info">
          Showing <strong>{totalShown}</strong> of <strong>{totalAll}</strong> orders
        </div>
      )}
    </div>
  );
};

/* ── Reusable card list ────────────────────────────────────── */
interface OrderCardsProps {
  orders: any[];
  loading: boolean;
  emptyMessage: string;
  emptySubMessage: string;
  onCardClick: (id: number) => void;
}

const OrderCards: FC<OrderCardsProps> = ({
  orders,
  loading,
  emptyMessage,
  emptySubMessage,
  onCardClick,
}) => {
  if (loading) {
    return <div className="ol-center"><Spin size="large" /></div>;
  }

  if (orders.length === 0) {
    return (
      <div className="ol-empty">
        <div className="ol-empty-icon">📦</div>
        <p className="ol-empty-title">{emptyMessage}</p>
        <p className="ol-empty-sub">{emptySubMessage}</p>
      </div>
    );
  }

  return (
    <div className="ol-list">
      {orders.map((order: any) => {
        const statusCfg    = getStatusConfig(order.deliveryStatus ?? order.orderStatus ?? order.status ?? "");
        const total        = order.totalPrice ?? order.amount ?? order.total;
        const payment      = order.paymentType ?? order.paymentMethod ?? "";
        const products     = order.perfumes ?? order.items ?? order.orderItems ?? [];
        const rawDate      = order.createdAt ?? order.date ?? order.orderDate;
        const dateStr = rawDate
          ? new Date(rawDate).toLocaleString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true, // use false if you want 24-hour format
            })
          : "—";
        const customerName = [order.firstName, order.lastName].filter(Boolean).join(" ") || null;

        return (
          <div
            key={order.id}
            className="ol-card"
            onClick={() => onCardClick(order.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onCardClick(order.id)}
          >
            {/* TOP ROW */}
            <div className="ol-card-top">
              <div className="ol-order-meta">
                <span className="ol-order-id">Order #{order.id}</span>
                <span className="ol-order-date">{dateStr}</span>
              </div>
              <span
                className="ol-status-badge"
                style={{ color: statusCfg.color, background: statusCfg.bg }}
              >
                {statusCfg.label}
              </span>
            </div>

            {/* CUSTOMER + LOCATION ROW */}
            <div className="ol-info-row">
              {customerName && (
                <span className="ol-info-item">
                  <span className="ol-info-label">Customer:</span> {customerName}
                </span>
              )}
              {order.city && (
                <span className="ol-info-item">
                  <span className="ol-info-label">City:</span> {order.city}
                </span>
              )}
              {order.postIndex && (
                <span className="ol-info-item">
                  <span className="ol-info-label">PIN:</span> {order.postIndex}
                </span>
              )}
              {order.phoneNumber && (
                <span className="ol-info-item">
                  <span className="ol-info-label">Phone:</span> {order.phoneNumber}
                </span>
              )}
            </div>

            {/* ADDRESS */}
            {order.address && (
              <div className="ol-address-row">
                <span className="ol-info-label">Address:</span>{" "}
                <span className="ol-address-text">{order.address}</span>
              </div>
            )}

            {/* PRODUCT THUMBNAILS */}
            {products.length > 0 && (
              <div className="ol-thumbs">
                {products.slice(0, 4).map((p: any, i: number) => (
                  <div key={i} className="ol-thumb-wrap">
                    <img
                      src={p.filename ?? p.image ?? p.imageUrl ?? ""}
                      alt={p.perfumeTitle ?? p.name ?? ""}
                      className="ol-thumb"
                    />
                  </div>
                ))}
                {products.length > 4 && (
                  <div className="ol-thumb-more">+{products.length - 4}</div>
                )}
              </div>
            )}

            {/* PRODUCT NAMES */}
            {products.length > 0 && (
              <div className="ol-product-names">
                {products.slice(0, 2).map((p: any, i: number) => (
                  <span key={i} className="ol-product-name">
                    {p.perfumeTitle ?? p.name}
                    {i === 0 && products.length > 1 ? "," : ""}
                  </span>
                ))}
                {products.length > 2 && (
                  <span className="ol-product-name muted">
                    +{products.length - 2} more items
                  </span>
                )}
              </div>
            )}

            {/* BOTTOM ROW */}
            <div className="ol-card-bottom">
              <div className="ol-payment-info">
                <span className="ol-total">
                  {total != null ? `₹${total}` : <span style={{ color: "#aaa", fontSize: 13 }}>Price N/A</span>}
                </span>
                <span className="ol-payment-type ol-qty">
                  Qty: {na(order.totalQuantity)}
                </span>
                {payment && (
                  <span className="ol-payment-type">
                    {payment.toUpperCase() === "COD" ? "Cash on Delivery" : "Online Payment"}
                  </span>
                )}
              </div>
              <RightOutlined className="ol-arrow" />
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── Paginated card list wrapper ─────────────────────────────── */
interface PaginatedOrderCardsProps extends OrderCardsProps {
  page: number;
  onPageChange: (page: number) => void;
}

const PaginatedOrderCards: FC<PaginatedOrderCardsProps> = ({
  orders,
  loading,
  emptyMessage,
  emptySubMessage,
  onCardClick,
  page,
  onPageChange,
}) => {
  const paged = paginate(orders, page, PAGE_SIZE);

  return (
    <>
      <OrderCards
        orders={paged}
        loading={loading}
        emptyMessage={emptyMessage}
        emptySubMessage={emptySubMessage}
        onCardClick={onCardClick}
      />
      {!loading && (
        <FlipkartPagination
          current={page}
          total={orders.length}
          pageSize={PAGE_SIZE}
          onChange={onPageChange}
        />
      )}
    </>
  );
};

/* ── Admin Order Tab ────────────────────────────────────────── */
type AdminTab = "DIST_REPLENISH" | "RETAIL";

/** Badge counts for a list of orders */
const getBadgeCount = (orders: any[], statusKey: string): number =>
  orders.filter(
    (o) =>
      (o.deliveryStatus ?? o.orderStatus ?? o.status ?? "").toUpperCase() === statusKey
  ).length;

interface AdminOrderTabProps {
  orderType: AdminTab;
  onCardClick: (id: number) => void;
}

const AdminOrderTab: FC<AdminOrderTabProps> = ({ orderType, onCardClick }) => {
  const [orders, setOrders]     = useState<any[]>([]);
  const [loading, setLoading]   = useState(false);
  const [filters, setFilters]   = useState<FilterState>(DEFAULT_FILTERS);
  const [page, setPage]         = useState(1);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all pages: start with page 0, keep fetching until we have all data
      let allOrders: any[] = [];
      let fetchPage = 0;
      const fetchSize = 200;

      while (true) {
        const url = `${BASE_URL}/api/v1/admin/orders?orderType=${orderType}&page=${fetchPage}&size=${fetchSize}`;
        const token = localStorage.getItem("token") ?? "";
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        // Support both array and paginated response shapes
        const items: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.content)
          ? data.content
          : [];

        allOrders = allOrders.concat(items);

        // If we got fewer items than fetchSize, we've reached the end
        if (items.length < fetchSize) break;

        // If the response includes total/totalElements, use that to check completeness
        const totalElements =
          data.totalElements ?? data.total ?? data.totalCount ?? null;
        if (totalElements !== null && allOrders.length >= totalElements) break;

        fetchPage++;

        // Safety cap: never fetch more than 50 pages (10 000 orders)
        if (fetchPage >= 50) break;
      }

      setOrders(allOrders);
    } catch (err) {
      console.error("AdminOrderTab fetch error:", err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [orderType]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Reset page when filters change
  const handleFilterChange = (f: FilterState) => {
    setFilters(f);
    setPage(1);
  };

  const filtered = applyFilters(orders, filters);

  // Badge: REQUESTED for distributor tab, ORDERED for retail tab
  const badgeStatus = orderType === "DIST_REPLENISH" ? "REQUESTED" : "ORDERED";
  const badgeCount  = getBadgeCount(orders, badgeStatus);

  const emptyMessage    = orderType === "DIST_REPLENISH" ? "No distributor orders" : "No retailer orders";
  const emptySubMessage = orderType === "DIST_REPLENISH"
    ? "Distributor replenishment orders will appear here"
    : "Retailer orders placed through the platform will appear here";

  return (
    <>
      {/* Badge alert strip */}
      {badgeCount > 0 && (
        <div className={`ol-badge-strip ol-badge-strip--${orderType === "DIST_REPLENISH" ? "requested" : "ordered"}`}>
          <span className="ol-badge-dot" />
          <strong>{badgeCount}</strong>&nbsp;
          {orderType === "DIST_REPLENISH" ? "Requested" : "Ordered"} order{badgeCount !== 1 ? "s" : ""} awaiting action
        </div>
      )}

      <FilterBar
        filters={filters}
        onChange={handleFilterChange}
        onReset={() => { setFilters(DEFAULT_FILTERS); setPage(1); }}
        totalShown={filtered.length}
        totalAll={orders.length}
      />
      <div className="ol-content">
        <PaginatedOrderCards
          orders={filtered}
          loading={loading}
          emptyMessage={emptyMessage}
          emptySubMessage={emptySubMessage}
          onCardClick={onCardClick}
          page={page}
          onPageChange={setPage}
        />
      </div>
    </>
  );
};

/* ── Main Component ─────────────────────────────────────────── */
const OrdersList: FC = (): ReactElement => {
  const dispatch  = useDispatch();
  const history   = useHistory();
  const orders    = useSelector(selectOrders);
  const isLoading = useSelector(selectIsOrdersLoading);

  const role    = localStorage.getItem("role") ?? "";
  const isAdmin =
    role === "1" ||
    role.toUpperCase() === "ADMIN" ||
    role.toUpperCase() === "ROLE_ADMIN" ||
    window.location.pathname.includes("/admin/");
  const isDistributor =
    role === "3" ||
    role.toUpperCase() === "DISTRIBUTOR" ||
    role.toUpperCase() === "ROLE_DISTRIBUTOR";

  // Admin tab state
  const [adminTab, setAdminTab] = useState<AdminTab>("DIST_REPLENISH");

  // Distributor-specific state
  const [activeTab, setActiveTab]             = useState<"retailer" | "my">("retailer");
  const [retailerOrders, setRetailerOrders]   = useState<any[]>([]);
  const [myOrders, setMyOrders]               = useState<any[]>([]);
  const [retailerLoading, setRetailerLoading] = useState(false);
  const [myLoading, setMyLoading]             = useState(false);
  const [retailerFilters, setRetailerFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [myFilters, setMyFilters]             = useState<FilterState>(DEFAULT_FILTERS);
  const [retailerPage, setRetailerPage]       = useState(1);
  const [myPage, setMyPage]                   = useState(1);
  const [customerPage, setCustomerPage]       = useState(1);

  const fetchTab = useCallback(
    async (orderType: "RETAIL" | "DIST_REPLENISH") => {
      const mobile = localStorage.getItem("mobile");
      if (!mobile) return;

      if (orderType === "RETAIL") setRetailerLoading(true);
      else setMyLoading(true);

      try {
        const result = await (dispatch(
          fetchDistributorOrders({ mobile, orderType })
        ) as any);
        const data = extractOrders(result?.payload);
        if (orderType === "RETAIL") setRetailerOrders(data);
        else setMyOrders(data);
      } finally {
        if (orderType === "RETAIL") setRetailerLoading(false);
        else setMyLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (isDistributor) {
      fetchTab("RETAIL");
      fetchTab("DIST_REPLENISH");
    } else if (!isAdmin) {
      dispatch(fetchAllUsersOrders(0));
    } else {
      dispatch(fetchAllUsersOrders(0));
    }
    return () => { dispatch(resetOrders()); };
  }, [dispatch, isDistributor, isAdmin, fetchTab]);

  const handleTabClick = (tab: "retailer" | "my") => {
    setActiveTab(tab);
    if (tab === "retailer") fetchTab("RETAIL");
    else fetchTab("DIST_REPLENISH");
  };

  const handleCardClick = (id: number) => {
    history.push(`/account/user/orders/${id}`);
  };

  /* ── Admin view ── */
  if (isAdmin) {
    return (
      <div className="ol-page">
        <div className="ol-header">
          <ShoppingOutlined className="ol-header-icon" />
          <span className="ol-header-title">All Orders</span>
        </div>

        <div className="ol-tabs">
          <button
            className={`ol-tab-btn ${adminTab === "DIST_REPLENISH" ? "active" : ""}`}
            onClick={() => setAdminTab("DIST_REPLENISH")}
          >
            Distributor Orders
          </button>
          <button
            className={`ol-tab-btn ${adminTab === "RETAIL" ? "active" : ""}`}
            onClick={() => setAdminTab("RETAIL")}
          >
            Retailer Orders
          </button>
        </div>

        {adminTab === "DIST_REPLENISH" ? (
          <AdminOrderTab key="DIST_REPLENISH" orderType="DIST_REPLENISH" onCardClick={handleCardClick} />
        ) : (
          <AdminOrderTab key="RETAIL" orderType="RETAIL" onCardClick={handleCardClick} />
        )}
      </div>
    );
  }

  /* ── Distributor view ── */
  if (isDistributor) {
    const activeOrders     = activeTab === "retailer" ? retailerOrders : myOrders;
    const activeLoading    = activeTab === "retailer" ? retailerLoading : myLoading;
    const activeFilters    = activeTab === "retailer" ? retailerFilters : myFilters;
    const setActiveFilters = activeTab === "retailer"
      ? (f: FilterState) => { setRetailerFilters(f); setRetailerPage(1); }
      : (f: FilterState) => { setMyFilters(f); setMyPage(1); };
    const activePage       = activeTab === "retailer" ? retailerPage : myPage;
    const setActivePage    = activeTab === "retailer" ? setRetailerPage : setMyPage;
    const filtered         = applyFilters(activeOrders, activeFilters);
    const totalCount       = retailerOrders.length + myOrders.length;

    // Badge counts: ORDERED for retailer tab, REQUESTED for my-orders tab
    const retailerBadge = getBadgeCount(retailerOrders, "ORDERED");
    const myBadge       = getBadgeCount(myOrders, "REQUESTED");

    return (
      <div className="ol-page">
        <div className="ol-header">
          <ShoppingOutlined className="ol-header-icon" />
          <span className="ol-header-title">Orders</span>
          {!retailerLoading && !myLoading && (
            <span className="ol-header-count">{totalCount} total</span>
          )}
        </div>

        <div className="ol-tabs">
          <button
            className={`ol-tab-btn ${activeTab === "retailer" ? "active" : ""}`}
            onClick={() => handleTabClick("retailer")}
          >
            Retailer Orders
            {!retailerLoading && (
              <span className="ol-tab-count">{retailerOrders.length}</span>
            )}
            {retailerLoading && <Spin size="small" style={{ marginLeft: 6 }} />}
            {!retailerLoading && retailerBadge > 0 && (
              <span className="ol-tab-badge ol-tab-badge--ordered">{retailerBadge}</span>
            )}
          </button>
          <button
            className={`ol-tab-btn ${activeTab === "my" ? "active" : ""}`}
            onClick={() => handleTabClick("my")}
          >
            My Orders
            {!myLoading && (
              <span className="ol-tab-count">{myOrders.length}</span>
            )}
            {myLoading && <Spin size="small" style={{ marginLeft: 6 }} />}
            {!myLoading && myBadge > 0 && (
              <span className="ol-tab-badge ol-tab-badge--requested">{myBadge}</span>
            )}
          </button>
        </div>

        {/* Badge strip for active tab */}
        {activeTab === "retailer" && retailerBadge > 0 && (
          <div className="ol-badge-strip ol-badge-strip--ordered">
            <span className="ol-badge-dot" />
            <strong>{retailerBadge}</strong>&nbsp;Ordered order{retailerBadge !== 1 ? "s" : ""} awaiting action
          </div>
        )}
        {activeTab === "my" && myBadge > 0 && (
          <div className="ol-badge-strip ol-badge-strip--requested">
            <span className="ol-badge-dot" />
            <strong>{myBadge}</strong>&nbsp;Requested order{myBadge !== 1 ? "s" : ""} awaiting action
          </div>
        )}

        <FilterBar
          filters={activeFilters}
          onChange={setActiveFilters}
          onReset={() => { setActiveFilters(DEFAULT_FILTERS); }}
          totalShown={filtered.length}
          totalAll={activeOrders.length}
        />

        <div className="ol-content">
          <PaginatedOrderCards
            orders={filtered}
            loading={activeLoading}
            emptyMessage={activeTab === "retailer" ? "No retailer orders yet" : "No replenishment orders yet"}
            emptySubMessage={
              activeTab === "retailer"
                ? "Retailer orders placed through your account will appear here"
                : "Your own stock replenishment orders will appear here"
            }
            onCardClick={handleCardClick}
            page={activePage}
            onPageChange={setActivePage}
          />
        </div>
      </div>
    );
  }

  /* ── Customer view ── */
  return (
    <div className="ol-page">
      <div className="ol-header">
        <ShoppingOutlined className="ol-header-icon" />
        <span className="ol-header-title">My Orders</span>
        {!isLoading && (
          <span className="ol-header-count">
            {orders.length} order{orders.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>
      <div className="ol-content">
        <PaginatedOrderCards
          orders={orders}
          loading={isLoading}
          emptyMessage="No orders yet"
          emptySubMessage="Your orders will appear here once you place them"
          onCardClick={handleCardClick}
          page={customerPage}
          onPageChange={setCustomerPage}
        />
      </div>
    </div>
  );
};

export default OrdersList;